import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';

const FeaturedModels = ({ onAddToCart, refreshTrigger }) => {
  const scrollContainerRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);
  const [favoriteModels, setFavoriteModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const authContext = useAuth();
  const user = authContext?.user || null;
  
  // Add direct session check fallback
  const [directUser, setDirectUser] = useState(null);
  const [sessionChecked, setSessionChecked] = useState(false);

  // Direct session check as fallback
  useEffect(() => {
    if (user) {
      setDirectUser(null);
      setSessionChecked(true);
      return;
    }

    if (!sessionChecked) {
      const checkDirectSession = async () => {
        try {
          const { data: { session } } = await supabase?.auth?.getSession();
          if (session?.user) {
            setDirectUser(session?.user);
          }
        } catch (err) {
          console.error('FeaturedModels - Direct session check failed:', err);
        } finally {
          setSessionChecked(true);
        }
      };

      checkDirectSession();
    }
  }, [user, sessionChecked]);

  // Use either AuthContext user or direct session user
  const effectiveUser = user || directUser;

  useEffect(() => {
    const fetchFavorites = async () => {
      // Wait for session check to complete
      if (!sessionChecked) {
        return;
      }

      if (!effectiveUser?.id) {
        console.log('FeaturedModels - No user found, hiding favorites');
        setFavoriteModels([]);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
        // Fetch user's favorited products from user_garage table
        const { data: favorites, error: favError } = await supabase
          ?.from('user_garage')
          ?.select(`
            id,
            product_id,
            component_id,
            item_type,
            products:product_id (
              id,
              name,
              price,
              thumbnail_path
            ),
            components:component_id (
              id,
              name,
              custom_name,
              price,
              manufacturer_part_number,
              product_id,
              product:products (id, name)
            )
          `)
          ?.eq('user_id', effectiveUser?.id);

        if (favError) {
          throw favError;
        }

        // Transform the data to match component expectations
        const transformedFavorites = favorites?.map(fav => {
          if (fav.item_type === 'product' && fav.products) {
            return {
              id: fav.products.id,
              name: fav.products.name,
              price: fav.products.price,
              image: fav.products.thumbnail_path,
              imageAlt: `${fav.products.name} thumbnail image`,
              isComponent: false,
            };
          } else if (fav.item_type === 'component' && fav.components) {
            const comp = fav.components;
            const baseName = comp.custom_name || comp.name;
            let displayName = baseName;
            
            // Composite naming: "Component for Product"
            if (comp.product?.name) {
                displayName = `${baseName} for ${comp.product.name}`;
            }

            return {
              id: comp.id,
              name: displayName,
              price: comp.price,
              image: `https://ui-avatars.com/api/?name=${encodeURIComponent(comp.name || 'Component')}&background=6366f1&color=fff&size=128`, // Placeholder image for components
              imageAlt: `${comp.name} thumbnail image`,
              isComponent: true,
              productId: comp.product_id,
            };
          }
          return null; // Should not happen if data is clean
        })?.filter(Boolean) || []; // Filter out any nulls

        setFavoriteModels(transformedFavorites);
      } catch (error) {
        console.error('FeaturedModels - Error in fetchFavorites:', error);
        setFavoriteModels([]);
      } finally {
        setLoading(false);
      }
    };

    fetchFavorites();
  }, [effectiveUser?.id, sessionChecked, effectiveUser, refreshTrigger]);

  const handleScroll = () => {
    if (!scrollContainerRef?.current) return;
    
    const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef?.current;
    setShowLeftArrow(scrollLeft > 0);
    setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
  };

  const scroll = (direction) => {
    if (!scrollContainerRef?.current) return;
    
    const scrollAmount = 300;
    const newScrollLeft = direction === 'left' 
      ? scrollContainerRef?.current?.scrollLeft - scrollAmount
      : scrollContainerRef?.current?.scrollLeft + scrollAmount;
    
    scrollContainerRef?.current?.scrollTo({
      left: newScrollLeft,
      behavior: 'smooth'
    });
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-6 shadow-soft">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (!favoriteModels || favoriteModels?.length === 0) {
    return null;
  }

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-soft">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-2">
          <Icon name="Heart" size={20} className="text-red-500 fill-red-500" />
          <h3 className="text-lg font-semibold text-foreground">Saved Favorites</h3>
        </div>
        
        <div className="flex items-center space-x-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => scroll('left')}
            disabled={!showLeftArrow}
            className="disabled:opacity-30"
          >
            <Icon name="ChevronLeft" size={18} />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => scroll('right')}
            disabled={!showRightArrow}
            className="disabled:opacity-30"
          >
            <Icon name="ChevronRight" size={18} />
          </Button>
        </div>
      </div>

      <div 
        ref={scrollContainerRef}
        onScroll={handleScroll}
        className="flex gap-6 overflow-x-auto scrollbar-hide scroll-smooth pb-2"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {favoriteModels?.map((model) => (
          <div
            key={model?.id}
            className="flex-shrink-0 flex flex-col items-center space-y-2 group"
          >
            {/* Icon-only display */}
            <Link to={model.isComponent ? `/3d-model-viewer?id=${model.productId}&componentId=${model.id}` : `/3d-model-viewer?id=${model.id}`}>
              <div className="w-24 h-24 bg-muted rounded-lg overflow-hidden hover:scale-110 transition-transform shadow-sm">
                <Image
                  src={model?.image}
                  alt={model?.imageAlt || model?.name}
                  className="w-full h-full object-cover"
                />
              </div>
            </Link>

            {/* Name and Price only */}
            <div className="text-center">
              <Link to={model.isComponent ? `/3d-model-viewer?id=${model.productId}&componentId=${model.id}` : `/3d-model-viewer?id=${model.id}`}>
                <p className="text-sm font-medium text-foreground hover:text-primary transition-colors line-clamp-1 max-w-[120px]">
                  {model?.name}
                </p>
              </Link>
              <p className="text-base font-bold text-foreground mt-1">
                ${model?.price?.toFixed(2)}
              </p>
              
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onAddToCart(model?.id, model?.isComponent)}
                className="mt-2 h-8 px-3"
              >
                <Icon name="ShoppingCart" size={14} />
                <span className="ml-1 text-xs">Add</span>
              </Button>
            </div>
          </div>
        ))}
      </div>

      {/* View All Link */}
      <div className="mt-6 text-center border-t border-border pt-4">
        <Link to="/model-catalog">
          <Button variant="ghost" size="sm">
            <span className="text-sm">View All Models</span>
            <Icon name="ArrowRight" size={14} className="ml-1" />
          </Button>
        </Link>
      </div>
    </div>
  );
};

export default FeaturedModels;